/**
 * Reading and writing the small preferences that outlive a tab — which day the board was
 * left on, which view, which crews were folded away, which board the nav was last pointed
 * at.
 *
 * Both halves swallow their errors, and that is the whole reason this is a function rather
 * than two calls at each site. Storage is not always there: a private window, a browser with
 * site data blocked, and — the one that actually bit us — a locked-down kiosk profile, where
 * even READING throws. The wall view had a guarded write and an unguarded read, and threw
 * during render on exactly the screens nobody can reach to debug.
 *
 * So nothing here is allowed to fail. A read that cannot happen gives the caller its
 * fallback and a write that cannot happen is dropped; the preference simply does not outlive
 * the tab, which is the correct behaviour for a preference.
 */

export function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key)
    return raw === null ? fallback : JSON.parse(raw)
  } catch {
    // Unreadable, or holding something that is no longer JSON — either way the fallback is
    // a better answer than a crash.
    return fallback
  }
}

export function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* private browsing, blocked site data, or a kiosk profile — it just won't outlive the tab */
  }
}

/**
 * The same pair over sessionStorage, for a preference that should last the session and no
 * longer — kept through a reload, gone when the tab is closed. Same contract: nothing here
 * may throw, for the same kiosk profiles.
 *
 * A session value outlives signing out, which reloads the same tab rather than closing it,
 * so a key for one person's preference has to name the person.
 */
export function readSessionJSON(key, fallback) {
  try {
    const raw = sessionStorage.getItem(key)
    return raw === null ? fallback : JSON.parse(raw)
  } catch {
    return fallback
  }
}

export function writeSessionJSON(key, value) {
  try {
    sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* unavailable — the preference lasts as long as the page instead */
  }
}
